import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Join Our Team', path: '/careers' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/90 backdrop-blur-lg border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="logo-link"
            className="flex items-center group"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_wealth-builders-6/artifacts/v43z5dyx_ChatGPT%20Image%20Mar%201%2C%202026%2C%2006_17_47%20PM.png"
              alt="Empower Wealth Builders"
              className="h-28 sm:h-32 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`relative font-body text-sm uppercase tracking-widest transition-colors duration-300 ${
                  isActive(link.path) 
                    ? 'text-gold-500' 
                    : 'text-white/70 hover:text-white'
                }`}
              >
                {link.name}
                {isActive(link.path) && (
                  <span className="absolute -bottom-1 left-0 w-full h-px bg-gold-500" />
                )}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <Link
            to="/careers"
            data-testid="nav-cta-btn"
            className="hidden md:block bg-gold-500 text-black px-6 py-2.5 font-body text-sm font-semibold uppercase tracking-widest hover:bg-gold-400 transition-colors duration-300"
          >
            Get Started
          </Link>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          data-testid="mobile-menu"
          className="md:hidden bg-black/95 backdrop-blur-lg border-t border-white/5"
        >
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block font-body text-base uppercase tracking-widest py-2 ${
                  isActive(link.path) 
                    ? 'text-gold-500' 
                    : 'text-white/70'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/careers"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block bg-gold-500 text-black px-6 py-3 font-body text-sm font-semibold uppercase tracking-widest text-center mt-4"
            >
              Get Started
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
